import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function AstraZenecaLiverCancerPage() {
  useSEO({
    title: 'AstraZeneca Liver Cancer Patient Support | Design For',
    description: 'Designing patient support programmes for liver cancer treatment. Strategic healthcare consulting for oncology patient engagement.',
    keywords: 'liver cancer, oncology, patient support, pharmaceutical consulting, cancer treatment',
    canonical: '/work/astrazeneca-liver-cancer',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Designing patient support programmes for liver cancer treatment',
    description: 'We partnered with AstraZeneca to develop comprehensive patient support programmes for liver cancer patients, improving treatment adherence and quality of life.',
    url: 'https://example.com/work/astrazeneca-liver-cancer',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Global pharmaceutical company - Oncology patient support',
    keywords: 'liver cancer, oncology, patient support, pharmaceutical consulting, cancer treatment',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Improving liver cancer treatment for patients &amp; doctors
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Top 10 pharmaceutical company</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                The company had a new cancer treatment coming to market and wanted to better support oncologists and their teams to get life changing treatment to patients who needed it most. Clinical data alone wasn&apos;t enough — they needed to understand the emotional, behavioural, and practical realities shaping treatment decisions across different markets.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                We set out to uncover these drivers through deep behavioural research, going beyond rational factors to understand how patients and clinicians actually experience diagnosis, treatment, and care. The work informed both strategic direction and practical tools that could be applied across markets.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Conducted 70+ in depth behavioural interviews with oncologists across the US, Germany, Japan, and France',
                  'Identified 10 core insights and themes shaping treatment decisions',
                  'Developed 9 behavioural patient archetypes and 6 oncology archetypes',
                  'Created current and future-state experience maps highlighting unmet needs and opportunities',
                  'Delivered 12 strategic business recommendations rolled out across commercial teams',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 rounded-2xl overflow-hidden min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/d654018d15e7499296428d0262ccfb1e.jpeg"
                  alt="AstraZeneca liver cancer treatment research and oncologist insights"
                  className="w-full h-full object-cover object-top"
                  style={{ minHeight: '340px' }}
                />
              </div>
              <div className="bg-gray-50 rounded-2xl overflow-hidden min-h-[340px] flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/0eb184b2ae5ad307ea8164c7353ed539.jpeg"
                  alt="AstraZeneca liver cancer treatment behavioural research insights"
                  className="w-full h-full object-cover object-top"
                  style={{ minHeight: '340px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="astrazeneca-liver-cancer" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
